// Role: Doctor, Researcher.
// Description: List of patients at home page.
import React from 'react';
import { withTranslation } from 'react-i18next';
import { PageWrapper, Paper, FlexTableRow } from 'virtues-bootstrap';
import PropTypes from 'prop-types';
import { map } from 'ramda';
import { requestPatientList } from 'services/api/request/patient';
import { getPatientsList } from 'store/selectors/patient';
import usePagination from 'hook/usePagination';
import PaginationSection from 'components/ui/PaginationSection';
import PaperWithLoader from 'components/ui/PaperWithLoader';
import HeaderColumn from 'components/ui/HeaderColumn';
import {
  RESEARCHER,
  SUPER_RESEARCHER,
  SUPER_TECHNICIAN,
  TECHNICIAN,
  DOCTOR,
} from 'constants/userRoles';
import PatientListRow from './components/PatientListRow';
import PatientListToolbar from './components/Toolbar';
import HeaderSearch from './components/HeaderSearch';
import { NAME, PATIENT_ID } from './constant';

import s from './styles.module.scss';

function PatientList({ role, userId, notificationsAdd, region, t }) {
  const isDoctor = role === DOCTOR;
  const isAvailableManagePatient = role === RESEARCHER || role === SUPER_RESEARCHER;
  const isClinicianAbility = !(role === SUPER_RESEARCHER || role === SUPER_TECHNICIAN);
  const isAvailableSearchByName = role === DOCTOR || role === RESEARCHER || role === TECHNICIAN;
  const generalPracitioner = role === SUPER_TECHNICIAN || role === SUPER_RESEARCHER ? null : userId;
  const [{ data, initial, fetching, pagination, error, sorting }, getData] = usePagination({
    request: requestPatientList,
    params: { generalPracitioner, sort: '_id', count: 12 },
    selector: getPatientsList,
  });
  const { total, page, count } = pagination;
  const { sort, desc } = sorting;

  const handlePageChange = selectedPage => {
    getData({
      pagesOffset: count * selectedPage,
      page: selectedPage,
    });
  };

  const searchFields = [
    ...(isAvailableSearchByName ? [{ key: t('patient.name'), value: NAME }] : []),
    { key: t('patient.id'), value: PATIENT_ID },
  ];
  const headerColumns = [
    ...(isClinicianAbility ? [{ title: t('patient.patientName'), key: 'name' }] : []),
    { title: t('patient.patientId'), key: '_id' },
    { title: t('patient.age'), key: 'birthdate' },
    { title: t('patient.deviceVendor') },
    { title: t('reports.deviceType') },
    { title: t('patient.activationDate') },
    ...(isDoctor ? [{ title: t('reports.recentReports'), key: 'recentReports' }] : []),
  ];

  return (
    <PageWrapper>
      {isAvailableManagePatient && (
        <PatientListToolbar
          isAblilityAddPatient={role === RESEARCHER}
          isAblilityUpdatePatientsSiteInformation={role === RESEARCHER}
          notificationsAdd={notificationsAdd}
          region={region}
        />
      )}
      <HeaderSearch searchFields={searchFields} onSubmit={getData} />
      <PaperWithLoader
        check={!!data.length}
        placeholder={t('patient.patientsNotFound')}
        error={error}
        loading={initial || fetching}
      >
        <Paper className={s.mainPaper} data-test="patientListBlock">
          <FlexTableRow heading>
            {headerColumns.map(({ title, key }) => (
              <HeaderColumn
                key={title}
                sort={sort}
                currentKey={key}
                title={title}
                desc={desc}
                handleClick={getData}
              />
            ))}
          </FlexTableRow>
          {map(
            ({ id, ...rest }) => (
              <PatientListRow
                key={id}
                id={id}
                isClinicianAbility={isClinicianAbility}
                isDoctor={isDoctor}
                {...rest}
              />
            ),
            data,
          )}
        </Paper>
        <PaginationSection
          activePage={page}
          disableInitialCallback
          onPageChange={handlePageChange}
          itemsPerPage={count}
          textKey="pagination.patients"
          total={total}
        />
      </PaperWithLoader>
    </PageWrapper>
  );
}

PatientList.propTypes = {
  userId: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  role: PropTypes.string.isRequired,
  notificationsAdd: PropTypes.func.isRequired,
  region: PropTypes.string.isRequired,
};

export default withTranslation()(PatientList);
